import { WsmErrorCode, WsmErrorFragment } from "@dashboard/graphql";
import commonErrorMessages, { getCommonFormFieldErrorMessage } from "@dashboard/utils/errors/common";
import { defineMessages, IntlShape } from "react-intl";

// The series members of WsmErrorCode. The backend sends its own merchant
// sentence in `message` and the Dashboard renders that verbatim; these strings
// are the fallback for a payload that carries a code and no sentence.
export const wsmSeriesErrorMessages = defineMessages({
  axisNotInAxes: {
    id: 'r6Ej5I',
    defaultMessage: "The partitioning axis has to be one of the axes ticked above",
  },
  seriesNeedsTwoMembers: {
    id: 'mfhhWy',
    defaultMessage: "A published series needs at least two published products",
  },
  memberMissingPartitioningAttribute: {
    id: 'cFe9uJ',
    defaultMessage: "Every published product in the series needs the partitioning attribute",
  },
  unknownAttributeSlug: {
    id: 'oBBerH',
    defaultMessage: "This store has no product attribute with that slug",
  },
  duplicatedProduct: {
    id: 'Z3X7sy',
    defaultMessage: "This series already holds that product",
  },
});

const codeToMessage = {
  [WsmErrorCode.AXIS_NOT_IN_AXES]: wsmSeriesErrorMessages.axisNotInAxes,
  [WsmErrorCode.SERIES_NEEDS_TWO_MEMBERS]: wsmSeriesErrorMessages.seriesNeedsTwoMembers,
  [WsmErrorCode.MEMBER_MISSING_PARTITIONING_ATTRIBUTE]:
    wsmSeriesErrorMessages.memberMissingPartitioningAttribute,
  [WsmErrorCode.UNKNOWN_ATTRIBUTE_SLUG]: wsmSeriesErrorMessages.unknownAttributeSlug,
  [WsmErrorCode.DUPLICATED_INPUT_ITEM]: wsmSeriesErrorMessages.duplicatedProduct,
} as Partial<Record<WsmErrorCode, (typeof wsmSeriesErrorMessages)["axisNotInAxes"]>>;

/**
 * The model's own sentence first, this module's wording second, the Dashboard's
 * common wording last. Returns undefined for no error, so a helperText prop can
 * take the result directly.
 */
export const getWsmSeriesErrorMessage = (
  error: WsmErrorFragment | undefined,
  intl: IntlShape,
): string | undefined => {
  if (!error) {
    return undefined;
  }

  if (error.message) {
    return error.message;
  }

  const message = error.code ? codeToMessage[error.code] : undefined;

  if (message) {
    return intl.formatMessage(message);
  }

  return getCommonFormFieldErrorMessage({ ...error, code: error.code }, intl);
};

export const wsmSeriesUnknownErrorMessage = commonErrorMessages.unknownError;

/** The error on a field, or on any row of it: `products.3` lands on the products picker. */
export const getWsmSeriesFieldError = (errors: WsmErrorFragment[], field: string) =>
  errors.find(error => error.field === field || error.field?.startsWith(`${field}.`));
